import type { Placement } from '@popperjs/core';
import { MixpanelEventSource, MixpanelSourceProvider } from '@talos/kyoko';
import { memo, useEffect, type RefObject } from 'react';
import { MarketSelectorContextProvider, useMarketSelectorContext } from './MarketSelectorContext';
import { MarketSelectorPanel } from './MarketSelectorPanel';
import { NarrowMarketSelectorPanel } from './NarrowMarketSelectorPanel';
import type { MarketSelectorAPI } from './types';
import { useMarketSelectorPanel } from './useMarketSelectorPanel';

export interface MarketSelectorPanelOnlyProps {
  marketSelectorAPI: MarketSelectorAPI;
  isOpen: boolean;
  onClose: () => void;
  panelAnchorRef: RefObject<HTMLElement>;
  panelPlacement?: Placement;
  panelOffset?: number;
  isNarrow?: boolean;
  showCredentialsOnlyToggle?: boolean;
}

/**
 * Renders only the Market Selector panel (dropdown) without the chrome (buttons, selections list).
 * Used for quick-access from menu/footer where we don't want the full MarketSelector UI.
 */
export const MarketSelectorPanelOnly = memo(function MarketSelectorPanelOnly({
  marketSelectorAPI,
  isOpen,
  onClose,
  panelAnchorRef,
  panelPlacement = 'auto',
  panelOffset = 8,
  isNarrow = false,
  showCredentialsOnlyToggle = false,
}: MarketSelectorPanelOnlyProps) {
  return (
    <MarketSelectorContextProvider
      marketSelectorAPI={marketSelectorAPI}
      isNarrow={isNarrow}
      showCredentialsOnlyToggle={showCredentialsOnlyToggle}
    >
      <MixpanelSourceProvider value={MixpanelEventSource.MarketAccountSelector}>
        <InnerPanelOnly
          isOpen={isOpen}
          onClose={onClose}
          panelAnchorRef={panelAnchorRef}
          panelPlacement={panelPlacement}
          panelOffset={panelOffset}
        />
      </MixpanelSourceProvider>
    </MarketSelectorContextProvider>
  );
});

interface InnerPanelOnlyProps {
  isOpen: boolean;
  onClose: () => void;
  panelAnchorRef: RefObject<HTMLElement>;
  panelPlacement: Placement;
  panelOffset: number;
}

function InnerPanelOnly({ isOpen, onClose, panelAnchorRef, panelPlacement, panelOffset }: InnerPanelOnlyProps) {
  const { isNarrow, setIsOpen } = useMarketSelectorContext();

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen, setIsOpen]);

  const dropdownProps = useMarketSelectorPanel({
    isOpen,
    close: onClose,
    panelAnchorRef,
    panelPlacement,
    panelOffset,
  });

  if (!isOpen) {
    return null;
  }

  return isNarrow ? (
    <NarrowMarketSelectorPanel dropdownProps={dropdownProps} />
  ) : (
    <MarketSelectorPanel dropdownProps={dropdownProps} />
  );
}
